import React, { useRef, useCallback } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { LinkNode, AutoLinkNode } from '@lexical/link'
import { ListNode, ListItemNode } from '@lexical/list'
import { CodeNode, CodeHighlightNode } from '@lexical/code'
import {
  TRANSFORMERS,
  $convertToMarkdownString,
  $convertFromMarkdownString,
} from '@lexical/markdown'
import { EditorState, $getRoot } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { Box } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ShowNotesTextBlock } from '../types'
import { TextBlockFloatingToolbar } from './text-block-floating-toolbar'
import { CitationPlugin } from './citation-plugin'
import { CitationNode, CITATION_TRANSFORMER } from './citation-node'

let TEXT_BLOCK_TRANSFORMERS = [...TRANSFORMERS, CITATION_TRANSFORMER]

// Simple editor theme
let editorTheme = {
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    code: 'editor-text-code',
  },
  citationA: 'editor-citation-a',
  citationB: 'editor-citation-b',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5',
    h6: 'editor-heading-h6',
  },
  list: {
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
  quote: 'editor-quote',
  code: 'editor-code',
  link: 'editor-link',
}

// Placeholder component
function Placeholder({ children }: { children: string }) {
  return (
    <div
      style={{
        color: '#999',
        fontSize: '16px',
        position: 'absolute',
        top: '16px',
        left: '16px',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  )
}

// Content initialization plugin
function ContentInitializationPlugin({
  initialMarkdown,
  editorRef,
}: {
  initialMarkdown: string
  editorRef?: React.MutableRefObject<any>
}) {
  let [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    if (editorRef) {
      // eslint-disable-next-line fp/no-mutation, no-param-reassign
      editorRef.current = editor
    }
  }, [editor, editorRef])

  React.useEffect(() => {
    if (initialMarkdown && initialMarkdown.trim()) {
      editor.update(() => {
        let root = $getRoot()
        root.clear()
        $convertFromMarkdownString(initialMarkdown, TEXT_BLOCK_TRANSFORMERS)
      })
    }
  }, [editor]) // Only run once on mount, not when markdown changes

  return null
}

interface TextBlockProps {
  block: ShowNotesTextBlock
  onChange: (block: ShowNotesTextBlock) => void
  placeholder?: string
  readOnly?: boolean
  existingCitations?: string[]
}

// Proper editor configuration matching the original
let editorConfig = {
  namespace: 'TextBlock',
  theme: editorTheme,
  onError: (error: Error) => {
    // eslint-disable-next-line no-console
    console.error('Lexical text block error:', error)
  },
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    LinkNode,
    AutoLinkNode,
    CitationNode,
  ],
  editorState: null, // Start with empty state, let ContentInitializationPlugin handle content
}

export function TextBlock({
  block,
  onChange,
  placeholder = 'Start writing...',
  readOnly = false,
  existingCitations = [],
}: TextBlockProps) {
  let lexicalEditorRef = useRef<any>(null)

  let handleChange = useCallback(
    (_: EditorState, editor: any) => {
      // Convert Lexical state to markdown
      let markdown = editor
        .getEditorState()
        .read(() => $convertToMarkdownString(TEXT_BLOCK_TRANSFORMERS))

      // Update the block with new markdown
      let updatedBlock: ShowNotesTextBlock = {
        ...block,
        markdown,
        updatedTime: new Date(),
      }

      onChange(updatedBlock)
    },
    [block, onChange],
  )

  // Handle clicks on the container to focus the editor
  let handleContainerClick = useCallback(
    (event: React.MouseEvent) => {
      // Focus the editor if clicking anywhere in the container that's not already focusable
      if (lexicalEditorRef.current && !readOnly) {
        let target = event.target as HTMLElement
        // Check if the click is on the contenteditable element or its children
        let isContentEditable =
          target.isContentEditable || target.closest('[contenteditable="true"]')

        // If not clicking directly on contenteditable area, focus the editor
        if (!isContentEditable) {
          lexicalEditorRef.current.focus()
        }
      }
    },
    [readOnly],
  )

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        position: 'relative',
        minHeight: '120px',
        backgroundColor: (theme) => (theme.palette.background as any).paperDark,
        borderRadius: 1,
        padding: 2,
        cursor: readOnly ? 'default' : 'text',
        '& .editor-input': {
          minHeight: '120px',
          padding: '0',
          fontSize: '16px',
          lineHeight: 1.6,
          outline: 'none',
          border: 'none',
          resize: 'none',
          fontFamily: 'inherit',
          color: 'inherit',
          backgroundColor: 'transparent',
        },
        // Make ContentEditable fill the entire container
        '& [contenteditable="true"]': {
          minHeight: '120px',
          width: '100%',
          outline: 'none',
          padding: '0',
          margin: '0',
          cursor: 'text',
        },
        '& .editor-text-bold': { fontWeight: 'bold' },
        '& .editor-text-italic': { fontStyle: 'italic' },
        '& .editor-text-underline': { textDecoration: 'underline' },
        '& .editor-text-strikethrough': { textDecoration: 'line-through' },
        '& .editor-text-code': {
          backgroundColor: (theme) => theme.palette.action.hover,
          padding: '2px 4px',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& .editor-citation-a': {
          backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
          borderRadius: '3px',
        },
        '& .editor-citation-b': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
          borderRadius: '3px',
        },
        '& .editor-heading-h1': {
          fontSize: '2em',
          fontWeight: 'bold',
          margin: '0.5em 0',
        },
        '& .editor-heading-h2': {
          fontSize: '1.5em',
          fontWeight: 'bold',
          margin: '0.5em 0',
        },
        '& .editor-heading-h3': {
          fontSize: '1.25em',
          fontWeight: 'bold',
          margin: '0.5em 0',
        },
        // List styles - comprehensive styling to ensure bullet points show
        '& .editor-list-ol, & .editor-list-ul': {
          margin: '0 0 1.5em 0 !important',
          paddingLeft: '2em !important',
          color: 'inherit !important',
          listStyle: 'initial !important',

          '& li': {
            color: 'inherit !important',
            display: 'list-item !important',
            listStyle: 'inherit !important',
            margin: '0.5em 0',
          },

          '& li::marker': {
            color: 'inherit !important',
          },
        },
        '& .editor-list-ol': {
          listStyleType: 'decimal !important',
        },
        '& .editor-list-ul': {
          listStyleType: 'disc !important',
        },
        '& .editor-listitem': {
          margin: '0.5em 0',
          color: 'inherit',
        },
        '& .editor-quote': {
          borderLeft: '4px solid #ccc',
          paddingLeft: '16px',
          margin: '16px 0',
          fontStyle: 'italic',
        },
        '& .editor-link': {
          color: (theme) => theme.palette.primary.main,
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      }}
    >
      <LexicalComposer initialConfig={editorConfig}>
        <ContentInitializationPlugin
          initialMarkdown={block.markdown}
          editorRef={lexicalEditorRef}
        />

        <RichTextPlugin
          contentEditable={
            <ContentEditable style={{ outline: 'none' }} readOnly={readOnly} />
          }
          placeholder={<Placeholder>{placeholder}</Placeholder>}
          ErrorBoundary={LexicalErrorBoundary}
        />

        <HistoryPlugin />
        <ListPlugin />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />

        <OnChangePlugin onChange={handleChange} ignoreSelectionChange={true} />

        <TextBlockFloatingToolbar />
        <CitationPlugin existingCitations={existingCitations} />
      </LexicalComposer>
    </Box>
  )
}

export default TextBlock
